import { Params, Path } from "@/router";
import { LinkProps } from "@generouted/react-router/client";
import Icon from "@/components/Icon";
import IconButton from "@/components/IconButton";
import { Link } from "@/router";
import { generatePath, useNavigate } from "react-router-dom";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { COMPACT_NAVBAR_REDESIGN_TOP_ACTION_BAR_HEIGHT } from "@/assets/theme/sizes";

type BaseProps<P extends Path> = {
  backLink: LinkProps<P, Params>;
  /** Reduces top spacing when rendered above a compact PageHeading */
  compact?: boolean;
  /** Override back navigation in compact mode (e.g., for returnTo logic) */
  onBackClick?: () => void;
};

type DesktopProps<P extends Path> = BaseProps<P> & {
  /** Desktop link label (required unless compactOnly) */
  title: string;
  /** Title shown in the compact sticky bar when it differs from the desktop link label */
  compactTitle?: string;
  compactOnly?: false;
};

type CompactOnlyProps<P extends Path> = BaseProps<P> & {
  /** Display title for the compact sticky bar (required for compactOnly) */
  compactTitle: string;
  title?: string;
  /** When true, renders only in compact mode and nothing on desktop */
  compactOnly: true;
};

export type Props<P extends Path> = DesktopProps<P> | CompactOnlyProps<P>;

/**
 * Responsive back-navigation for interior/detail pages. Desktop renders an
 * inline link; compact (< 1024px) renders a sticky bar with centered title.
 */
export default function Breadcrumb<P extends Path>({
  title,
  compactTitle,
  backLink,
  compact,
  onBackClick,
  compactOnly
}: Props<P>) {
  const { isCompactLayout } = useIsCompactLayout();
  const navigate = useNavigate();
  const stickyTop = COMPACT_NAVBAR_REDESIGN_TOP_ACTION_BAR_HEIGHT;

  if (isCompactLayout) {
    const handleBackClick = () => {
      if (onBackClick) {
        onBackClick();
      } else {
        const params = "params" in backLink ? backLink.params : undefined;
        navigate(params ? generatePath(backLink.to, params as any) : backLink.to);
      }
    };

    return (
      <div
        className="flex items-center justify-between bg-sys-brand-surface border-b border-sys-brand-outline-variant z-40 sticky"
        style={{
          top: `${stickyTop}px`,
          marginLeft: "-1rem",
          marginRight: "-1rem",
          paddingLeft: ".5rem",
          paddingRight: ".5rem"
        }}
        data-testid="breadcrumb"
      >
        <IconButton
          name="arrow_back"
          onClick={handleBackClick}
          data-testid="breadcrumb-back"
          variant="square"
          iconClassName="text-sys-on-surface"
        />
        <h1 className="text-sys-on-surface text-body-large-bold font-serif text-center flex-1 mx-2">
          {compactTitle || title}
        </h1>
        <div className="w-14 h-14" />
      </div>
    );
  }

  if (compactOnly) return null;

  return (
    <div className={`flex items-center ${compact ? "pt-8 sm:pt-4" : "pt-8"}`} data-testid="breadcrumb">
      <Link
        {...backLink}
        className="flex items-center gap-2 -m-2 p-2 rounded-lg hover:bg-sys-brand-on-surface/5 transition-colors"
        data-testid="breadcrumb-back"
      >
        <Icon name="arrow_back" size={20} className="text-sys-on-surface" />
        <span className="text-sys-on-surface text-title-medium-bold font-serif underline">{title}</span>
      </Link>
    </div>
  );
}
